import React, { useEffect, useMemo, useState } from 'react';
import { Form, Button, Input, Spin, Typography, Select, notification, Tooltip, Tag } from 'antd';
const { Text } = Typography;
import { ReactSVG } from 'react-svg';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { CopyOutlined, QuestionCircleTwoTone } from '@ant-design/icons';
import { doGetLocations } from '../../../../modules/locations/stores/thunk';
import { selectAllLocations } from '../../../../modules/locations/stores/slice';
import copy from 'copy-to-clipboard';

import {
  doCreateProviderIntegration,
  doDeleteProviderIntegration,
  doGetProviderIntegration,
  doUpdateProviderIntegration,
} from '../../../../modules/provider-integrations/store/thunk';
import { clearState } from '../../../../modules/provider-integrations/store/slice';

const How = ({ title, tooltip }) => {
  return (
    <>
      <span>{title}</span>
      <Tooltip title={tooltip}>
        <QuestionCircleTwoTone style={{ marginLeft: 8 }} />
      </Tooltip>
    </>
  );
};
export const SetupShopify = ({ isFetching, done }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const [locationId, setLocationId] = useState(null);

  const [copied, setCopied] = useState(false);
  const [fetchingIntegration, setFetchingIntegration] = useState(false);

  const locations = useSelector((state) => selectAllLocations(state));
  const [existing, setExisting] = useState(null);
  const locationOptions = locations.map((l) => ({ label: l.name, value: l.locationId }));

  const [isSaving, setIsSaving] = useState(false);
  const [values, setValues] = useState({
    SIGNATURE: null,
    STORE_DOMAIN: null,
    API_KEY: null,
  });

  const disabled = useMemo(
    () =>
      [
        'SIGNATURE',
        // 'API_KEY',
        'STORE_DOMAIN',
      ].some((k) => !values[k]),
    [copied, values],
  );

  const handleCancel = () => {
    form.resetFields();
    done();
  };

  const webhookUrl = useMemo(
    () =>
      locationId
        ? `${process.env.REACT_APP_API_URI}/integrations/shopify?locationId=${locationId}`
        : 'URL will be provided after you have selected a location...',
    [locationId],
  );

  useEffect(() => {
    dispatch(clearState());
    dispatch(doGetLocations({ page: 1, take: 1000 }));
  }, []);

  useEffect(() => {
    const getIt = async () => {
      setFetchingIntegration(true);
      const res = await dispatch(
        doGetProviderIntegration({
          locationId: locations.find((l) => l.locationId === locationId)?.id,
          service: 'shopify',
        }),
      );

      setExisting(res.payload);

      setFetchingIntegration(false);
      const newValues = {};
      ['SIGNATURE', 'STORE_DOMAIN', 'API_KEY'].forEach((k) => {
        const v = res?.payload?.credentials?.[k];
        if (v) {
          form.resetFields([k]);
          form.setFieldValue(k, v);
          newValues[k] = v;
        } else {
          form.resetFields([k]);
        }
      });
      setValues(newValues);
    };
    if (locationId) {
      getIt();
    }
    return () => dispatch(clearState()); // explicitly clear the state to prevent it from being used in different component
  }, [locationId]);

  const submit = async (values) => {
    setIsSaving(true);
    // construct param objects from input
    const update = {
      id: existing?.id || null,
      credentials: {
        SIGNATURE: values.SIGNATURE || null,
        API_KEY: values.API_KEY || null,
        STORE_DOMAIN: values.STORE_DOMAIN || null,
      },
      service: 'shopify',
      locationId: locations.find((l) => l.locationId === locationId)?.id,
      companyId: locations.find((l) => l.locationId === locationId).companyId,
    };

    // send updates or creates to backend depending on their existance
    if (update.id && update.credentials?.STORE_DOMAIN) {
      // if it existed and has a value => update it
      await dispatch(doUpdateProviderIntegration(update));
    } else if (update.id && !update.credentials?.STORE_DOMAIN) {
      // if it existed and does not have a value => delete it
      await dispatch(doDeleteProviderIntegration(update));
    } else if (update.credentials) {
      // if it didn't exist => create it
      await dispatch(doCreateProviderIntegration(update));
    }

    done({ refetch: true });
  };

  const onClickCopy = (value) => {
    setCopied(true);
    copy(value);
    notification.success({ message: 'Copied Shopify webhook URL' });
  };

  const spinning = useMemo(
    () => isFetching || isSaving || fetchingIntegration,
    [isFetching, isSaving, fetchingIntegration],
  );

  return (
    <Spin spinning={spinning}>
      <div className="RoutePlanForm">
        <div>
          <h4>
            1. Select your location
            <Tooltip title="Select the location that you want your shopify store to create orders for">
              <QuestionCircleTwoTone style={{ marginLeft: 8 }} />
            </Tooltip>
          </h4>

          <Select
            className="select-small"
            options={locationOptions}
            onSelect={(value) => {
              setCopied(false);
              setLocationId(value);
            }}
            style={{ minWidth: '200px' }}
          />
        </div>
        <div>
          <h4>
            2. Setup shopify webhook{' '}
            <Tooltip title="Copy this webhook URL and set it in your shopify store webhook settings.">
              <QuestionCircleTwoTone style={{ marginLeft: 8 }} />
            </Tooltip>
          </h4>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text code disabled={!locationId}>
              {webhookUrl}
            </Text>
            <Button size="small" type="primary" onClick={() => onClickCopy(webhookUrl)} disabled={!locationId}>
              <CopyOutlined style={{ paddingTop: 4 }} />
              Copy URL
            </Button>
          </div>
        </div>

        <Form form={form} onFinish={submit} layout="vertical" requiredMark={false} colon={false}>
          <Form.Item
            label={
              <How
                title="3. Shopify signature"
                tooltip="Copy the signature from your shopify webhook settings page and paste it here."
              />
            }
            value={values.SIGNATURE}
            initialValue={values.SIGNATURE}
            name="SIGNATURE"
          >
            <Input
              placeholder="Your Shopify signature"
              size="large"
              onChange={(e) => setValues({ ...values, SIGNATURE: e.target.value })}
              disabled={!values.SIGNATURE && (!locationId || !copied)}
            />
          </Form.Item>

          {existing && existing.config?.TESTED && (
            <div style={{ display: 'flex', justifyContent: 'right' }}>
              <Tag style={{ marginBottom: 8 }} color="green">
                This integration is active and was tested successfully
              </Tag>
            </div>
          )}
          {existing && !existing.config?.TESTED && (
            <div style={{ display: 'flex', justifyContent: 'right' }}>
              <Tooltip title="Send a test order from the shopify webhook settings page.">
                <Tag style={{ marginBottom: 8 }} color="orange">
                  Integration settings were saved but not tested yet
                </Tag>
              </Tooltip>
            </div>
          )}
          <Form.Item
            label={
              <How
                title="4. Shopify store domain"
                tooltip="Please provide the domain of your Shopify store as displayed in your shopify admin portal."
              />
            }
            value={values.STORE_DOMAIN}
            initialValue={values.STORE_DOMAIN}
            name="STORE_DOMAIN"
          >
            <Input
              placeholder="Your Shopify Store Domain"
              size="large"
              onChange={(e) => setValues({ ...values, STORE_DOMAIN: e.target.value })}
              disabled={!values.SIGNATURE}
            />
          </Form.Item>

          <Form.Item
            label={
              <How
                title="5. Shopify API key"
                tooltip="Please provide the API key as as retrieved after creating a your private app."
              />
            }
            value={values.API_KEY}
            initialValue={values.API_KEY}
            name="API_KEY"
          >
            <Input
              placeholder="Your Shopify Private App API KEY"
              size="large"
              onChange={(e) => setValues({ ...values, API_KEY: e.target.value })}
              disabled={!values.SIGNATURE}
            />
          </Form.Item>
          <div className="FormFooter">
            <Button key="cancel" size="small" onClick={handleCancel}>
              Cancel
            </Button>

            <Button key="confirm" type="primary" size="small" htmlType="submit" disabled={disabled}>
              {existing ? 'Update' : 'Add'} Shopify integration
              <ReactSVG className="icon" src="/icons/done.svg" />
            </Button>
          </div>
        </Form>
      </div>
    </Spin>
  );
};
